import {
  Fragment,
  useEffect,
  useState,
  useRef,
  useMemo,
  createContext,
  useContext,
} from "react";
import { Tab } from "@headlessui/react";
import {
  Input,
  Checkbox,
  Calendar,
  DateInput,
  useDateInput,
  NoMatchingResultIllustration,
  SearchIllustration,
  cn,
  Slot,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  Button,
  useCombobox,
} from "ui";
import { format, isSameYear, parse, subDays } from "date-fns";
import { backendKeyMap } from "../utils/backendKeyMap";
import { snakeCaseToHumanReadable } from "../utils/snakeCaseToHumanReadable";
import { arrify } from "../utils/arrify";
import { axiosApiInstance } from "../pages/api/axios";
import {
  BadgeCheckIcon,
  CalendarIcon,
  ChipIcon,
  ClockIcon,
  CloudIcon,
  CodeCircleIcon,
  DocumentCodeIcon,
  DocumentMetaIcon,
  DollarCircleIcon,
  DollarSquareIcon,
  EmptyWalletTickIcon,
  FilterIcon,
  GlobeAltIcon,
  HierarchySquare2Icon,
  LibraryIcon,
  MoneyReciveIcon,
  MoneySendIcon,
  OptimizationIcon,
  PaymentProviderIcon,
  PercentageSquareIcon,
  RefreshIcon,
  SearchIcon,
  SecurityCardIcon,
  StatusOnlineIcon,
  TransactionsIcon,
  UserCircleIcon,
  UsersIcon,
  WalletIcon,
  XCircleIcon,
  XIcon,
  PayInProviderIcon,
  PhoneIcon,
  MailIcon,
  DocumentTextIcon,
  GlobeIcon,
  CardLookupIcon,
  CardEditIcon,
  ProviderLookupIcon,
} from "icons/outline";
import { useRouter } from "next/router";
import { useMoneyHashQuery } from "../hooks/useMoneyHashQuery";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useLocalStorage } from "react-use";
import useUpdateEffect from "../hooks/useUpdateEffect";
import { isValidDate } from "../utils/isValidDate";

const ChoicesList = ({
  choices,
  isMultiple,
  isSelected,
  render,
  getMenuProps,
  getItemProps,
  highlightedIndex,
  showOptions,
  children,
  recentOptions,
}) => {
  return (
    <ul
      className="flex-1  overflow-y-scroll px-2"
      tabIndex={-1}
      {...getMenuProps?.()}
    >
      {recentOptions?.length > 0 && (
        <>
          <li className="px-2 pb-2 pt-3 text-[11px] font-medium uppercase text-foundations-bold">
            Recents
          </li>
          {recentOptions.map((item, index) => {
            const [value, name] = item;
            const isHighlighted = highlightedIndex === index;

            return (
              <li
                className={cn(
                  "flex cursor-pointer items-center justify-between rounded px-2 py-2 text-sm text-foundations-bolder",
                  isHighlighted && "bg-foundations-subtlest"
                )}
                key={value}
                {...getItemProps?.({ item, index })}
              >
                {render(name)}
              </li>
            );
          })}
          <li
            role="presentation"
            aria-hidden
            className="-mx-2 border-b border-foundations-subtle pb-1"
          />
        </>
      )}

      {showOptions && recentOptions && (
        <li className="px-2 pb-2 pt-3 text-[11px] font-medium uppercase text-foundations-bold">
          all filters
        </li>
      )}

      {showOptions ? (
        choices?.length ? (
          choices.map((item, index) => {
            const [value, name] = item;
            const optionIndex = index + (recentOptions?.length || 0);
            const isHighlighted = highlightedIndex === optionIndex;
            const isSelectedValue = isSelected?.(value);

            return name ? (
              <li
                className={cn(
                  "flex cursor-pointer items-center justify-between rounded px-2 py-2 text-sm text-foundations-bolder",

                  !isMultiple &&
                    isSelectedValue &&
                    "bg-info-bold text-foundations-inverse",
                  isHighlighted && "bg-foundations-subtlest",

                  !isMultiple &&
                    isSelectedValue &&
                    isHighlighted &&
                    "bg-info-bolder"
                )}
                key={value}
                {...getItemProps?.({ item, index: optionIndex })}
              >
                {render ? (
                  render(name)
                ) : (
                  <>
                    <span>{snakeCaseToHumanReadable(name)}</span>
                    {isMultiple && (
                      <Checkbox
                        tabIndex={-1}
                        readOnly
                        checked={isSelected(value)}
                      />
                    )}
                  </>
                )}
              </li>
            ) : null;
          })
        ) : (
          <div className="my-4 flex flex-col items-center space-y-4">
            <NoMatchingResultIllustration />
            <p className="text-sm text-foundations-subtle">
              No matching data available
            </p>
          </div>
        )
      ) : (
        children
      )}
    </ul>
  );
};

const SearchField = ({
  filter,
  selectedValue,
  isMultiple,
  isSelected,
  handleSelect,
  setIsOpen,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 450);
  const { dependantFilter, filters } = useFiltersContext();
  const filterByFilter = filters.find(
    (f) => f.field_name === dependantFilter?.filterBy
  );

  const { data: options = [], isLoading } = useMoneyHashQuery({
    queryKey: [
      "filter",
      {
        name: filter.field_name,
        path: filter.path,
        searchTerm: debouncedSearchTerm,
        param: filter.search_param,
        dependantFilter,
        dependantFilterName: dependantFilter?.name,
        dependantFilterFilterBy: dependantFilter?.filterBy,
        filterByFilterSelectedValue: filterByFilter?.selected_value,
      },
    ],
    queryFn: ({ signal }) =>
      axiosApiInstance
        .get(`${process.env.NEXT_PUBLIC_BACKEND_URL}${filter.path}`, {
          params: {
            [filter.search_param]: debouncedSearchTerm,
            ...(dependantFilter &&
              filter.field_name === dependantFilter.name && {
                [dependantFilter.filterBy]:
                  filterByFilter?.selected_value || undefined,
              }),
            offset: 0,
            limit: 10,
          },
          signal,
        })
        .then((res) => ({
          data: Array.isArray(res.data.data) ? res.data.data : [],
        })),
    cacheTime: 60_000, // 1 minute
    enabled: !!debouncedSearchTerm,
  });

  return (
    <Combobox
      options={options}
      selectedValue={selectedValue}
      isMultiple={isMultiple}
      onSelect={(item) => handleSelect(item?.[0])}
      inputValue={searchTerm}
      onInputValueChange={setSearchTerm}
      selected={selectedValue}
      setIsOpen={setIsOpen}
    >
      <div className="px-4 pb-2">
        <Combobox.Input />
      </div>
      <div className="flex-1 overflow-y-auto">
        <Combobox.Options
          isSelected={isSelected}
          showOptions={debouncedSearchTerm && !isLoading}
        >
          {!debouncedSearchTerm ? (
            <div className="my-4 flex flex-col items-center space-y-4">
              <SearchIllustration />
              <p className="text-center text-sm text-foundations-subtle">
                Please type a search keyword
              </p>
            </div>
          ) : isLoading ? (
            <div className="my-4 flex flex-col items-center">
              <SearchIllustration />
            </div>
          ) : null}
        </Combobox.Options>
      </div>
    </Combobox>
  );
};

const multipleFieldsSet = new Set([
  "MultipleChoiceField",
  "ModelMultipleChoiceField",
  "MultiSelectSearchField",
]);

const fieldsWithoutFooterSet = new Set([
  "ChoiceField",
  "ModelChoiceField",
  "NullBooleanField",
  "DateField",
  "DateTimeField",
  "SearchField",
]);

const nullBooleanFieldOptions = [
  ["true", "Yes"],
  ["false", "No"],
];

const FilterInput = ({
  filter,
  value,
  onApplyFilter,
  shouldShowFooter,
  setOpen,
  isMultiple,
}) => {
  const searchInputRef = useRef(null);

  const updateFilter = (name, updatedValue) => {
    onApplyFilter(name, value === updatedValue ? "" : updatedValue);

    if (!shouldShowFooter) {
      setOpen(false);
    }
  };

  const isSelected = (optionValue) =>
    arrify(value)?.some?.((v) => v?.toString() === optionValue?.toString());

  const handleSelect = (selectedValue) => {
    if (!isMultiple) {
      updateFilter(filter.field_name, selectedValue);
      return;
    }

    if (isSelected(selectedValue)) {
      const selectedOptionsUpdated = arrify(value).filter(
        (v) => v !== selectedValue
      );

      updateFilter(filter.field_name, selectedOptionsUpdated);
      return;
    }

    const selectedOptionsUpdated = [...arrify(value), selectedValue];
    updateFilter(filter.field_name, selectedOptionsUpdated);
  };

  useEffect(() => {
    if (!searchInputRef.current) return;
    searchInputRef.current.focus();
  }, [searchInputRef]);

  if (filter.field_type === "CharField") {
    return (
      <div className="mb-2 px-4">
        <Input
          autoFocus
          type="text"
          placeholder="Add text..."
          value={value || ""}
          onChange={(value) => {
            updateFilter(filter.field_name, value);
          }}
        />
      </div>
    );
  } else if (filter.field_type === "DecimalField") {
    return (
      <div className="mb-2 px-4">
        <Input
          autoFocus
          type="number"
          value={value || ""}
          placeholder="Enter number..."
          onChange={(value) => {
            updateFilter(filter.field_name, value);
          }}
        />
      </div>
    );
  } else if (
    filter.field_type === "DateTimeField" ||
    filter.field_type === "DateField"
  ) {
    return (
      <DateField
        value={value}
        updateFilter={updateFilter}
        filter={filter}
        close={() => setOpen(false)}
      />
    );
  } else if (
    filter.field_type === "ChoiceField" ||
    filter.field_type === "MultipleChoiceField" ||
    filter.field_type === "ModelChoiceField" ||
    filter.field_type === "ModelMultipleChoiceField"
  ) {
    return (
      <Combobox
        isMultiple={isMultiple}
        options={filter.choices}
        filterFn={([_value, name], searchTerm) => {
          // NOTE: convert to string as some values are non-string
          return name
            ?.toString()
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase());
        }}
        selected={value}
        onSelect={(item) => {
          handleSelect(item?.[0]);
        }}
        setIsOpen={setOpen}
      >
        <div className="px-4 pb-2">
          <Combobox.Input />
        </div>

        <Combobox.Options isSelected={isSelected} />
      </Combobox>
    );
  } else if (filter.field_type === "NullBooleanField") {
    return (
      <Combobox
        options={nullBooleanFieldOptions}
        filterFn={([_value, name], searchTerm) => {
          // NOTE: convert to string as some values are non-string
          return name
            ?.toString()
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase());
        }}
        selected={value}
        onSelect={(item) => {
          handleSelect(item?.[0]);
        }}
        setIsOpen={setOpen}
      >
        <div className="px-4 pb-2">
          <Combobox.Input />
        </div>

        <Combobox.Options isSelected={isSelected} />
      </Combobox>
    );
  } else if (
    ["SearchField", "MultiSelectSearchField"].includes(filter.field_type)
  ) {
    return (
      <SearchField
        filter={filter}
        selectedValue={value}
        isSelected={isSelected}
        handleSelect={handleSelect}
        isMultiple={isMultiple}
        setIsOpen={setOpen}
      />
    );
  }
};

function FilterChip({ children, asChild, className, interactive }) {
  const Component = asChild ? Slot : "div";
  return (
    <Component
      className={cn(
        "border border-foundations-subtle bg-foundations-default px-2 py-1 outline-none first:rounded-l-lg last:rounded-r-lg",
        interactive &&
          "hover:z-10 hover:border-foundations-bold hover:bg-foundations-subtlest focus-visible:z-10 focus-visible:border-info-bold focus-visible:ring-1 focus-visible:ring-info-bold",
        className
      )}
    >
      {children}
    </Component>
  );
}

function FilterField({
  filter,
  value,
  onRemoveFilter,
  onApplyFilter,
  emptyFallback = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { nonClearableFilters } = useFiltersContext();
  const clearable = !nonClearableFilters?.includes(filter.field_name);

  const isMultiple = multipleFieldsSet.has(filter.field_type);

  const label =
    backendKeyMap[filter.field_name] ??
    snakeCaseToHumanReadable(filter.field_name);

  const displayedValue = useMemo(() => {
    if (!value) return null;

    if (filter.field_type === "SearchField") {
      return filter.value_lookup;
    }

    if (filter.field_type === "MultiSelectSearchField") {
      return filter.multiple_selected_values.map((v) => Object.values(v)[0]);
    }

    if (filter.field_type === "NullBooleanField")
      return nullBooleanFieldOptions.find((option) => option[0] === value)?.[1];

    if (!filter.choices) return value;

    // Get option label corresponding to it's value
    if (!isMultiple)
      return filter.choices.find(
        // NOTE: convert to string as some values are non-string
        (option) => option[0]?.toString() === value?.toString()
      )?.[1];

    return arrify(value).map(
      (v) =>
        filter.choices.find(
          // NOTE: convert to string as some values are non-string
          (option) => option[0]?.toString() === v?.toString()
        )?.[1]
    );
  }, [filter, value, isMultiple]);

  const Icon = filterIcons[filter.field_name];

  return (
    <div className="flex h-6 shrink-0 text-xs font-medium text-foundations-subtle [&>:not(:first-child)]:-ml-px">
      <FilterChip className="flex items-center gap-1 capitalize">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </FilterChip>

      <FilterChip>is</FilterChip>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <FilterChip interactive asChild className="text-foundations-bold">
          <PopoverTrigger>
            {displayedValue ? (
              <>
                {isMultiple ? (
                  <MultiplePreview value={arrify(displayedValue)} />
                ) : (
                  snakeCaseToHumanReadable(displayedValue)
                )}
              </>
            ) : (
              emptyFallback
            )}
          </PopoverTrigger>
        </FilterChip>

        <FilterPopover>
          <FilterFieldContent
            label={label}
            value={value}
            filter={filter}
            onApplyFilter={onApplyFilter}
            setIsOpen={setIsOpen}
          />
        </FilterPopover>
      </Popover>

      {clearable && (
        <FilterChip
          interactive
          asChild
          className="p-1 text-foundations-subtler"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveFilter(filter.field_name);
            }}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </FilterChip>
      )}
    </div>
  );
}

const FilterPopover = ({ children, align = "start" }) => {
  return (
    <PopoverContent
      className="z-10 flex max-h-[var(--radix-popover-content-available-height)] min-w-80 max-w-80 flex-col overflow-hidden rounded-md bg-foundations-default shadow-2"
      align={align}
      side="bottom"
      sideOffset={8}
      collisionPadding={8}
    >
      {children}
    </PopoverContent>
  );
};

const FilterFieldContent = ({
  value,
  label,
  filter,
  onApplyFilter,
  setIsOpen,
}) => {
  const [tempValue, setTempValue] = useState(value);
  const isMultiple = multipleFieldsSet.has(filter.field_type);
  const shouldShowFooter = !fieldsWithoutFooterSet.has(filter.field_type);

  return (
    <>
      <div className="flex items-center justify-between p-4 pb-0">
        <div className="font-medium text-foundations-bolder">
          Filter by {label}
        </div>
        <PopoverClose className="text-foundations-subtler hover:text-foundations-subtle focus:text-info focus:outline-none">
          <XCircleIcon className="h-5 w-5" />
        </PopoverClose>
      </div>

      <form
        id={`form-${label}`}
        className={cn(
          "flex flex-1 flex-col overflow-hidden pt-2",
          shouldShowFooter ? "" : "pb-2"
        )}
        onSubmit={(e) => {
          e.preventDefault();
          onApplyFilter(filter.field_name, tempValue);
          setIsOpen(false);
        }}
      >
        <FilterInput
          filter={filter}
          value={tempValue}
          onApplyFilter={(filter, value) => {
            setTempValue(value);
            if (!shouldShowFooter) {
              onApplyFilter(filter, value);
            }
          }}
          shouldShowFooter={shouldShowFooter}
          setOpen={setIsOpen}
          isMultiple={isMultiple}
        />
      </form>
      {shouldShowFooter && (
        <div className="mt-1 flex gap-3 border-t border-foundations-subtle p-4">
          {isMultiple && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setTempValue("");
              }}
              className="flex-1"
            >
              Reset
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
            form={`form-${label}`}
          >
            Apply
          </Button>
        </div>
      )}
    </>
  );
};

const MultiplePreview = ({ value }) => {
  if (!value?.length) return "";
  if (value.length === 1) {
    return value[0];
  }

  return (
    <>
      {value[0]} <span className="lowercase">and {value.length - 1} more</span>
    </>
  );
};

// custom intent & transactions date filters
const customIntentAndTrxDateFilters = ["created_after", "created_before"];
const MAX_RECENT_FILTERS = 3;

const FiltersContext = createContext(null);

export const useFiltersContext = () => {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error(
      "useFiltersContext must be used within <FiltersProvider />"
    );
  }

  return context;
};

const FiltersProvider = ({ children, value }) => {
  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  );
};

/**
 * Filters component
 *
 * @param {Object} props - Component props
 * @param {any} props.filters - The filters to display or apply
 * @param {string} props.recentStoreKey - Key used for storing/retrieving recent filters
 * @param {string[]} [props.customDateFilters=customIntentAndTrxDateFilters] - Optional custom date filters
 * @param {"default" | "dashboard"} [props.filtersFor="default"] - Context for which filters apply
 * @param {string[]} [props.nonClearableFilters] - List of filter keys that cannot be cleared
 * @param {{name: string, filterBy: string}} [props.dependantFilter] - Optional filter dependency
 */
export const Filters = ({
  filters,
  recentStoreKey,
  customDateFilters = customIntentAndTrxDateFilters,
  filtersFor = "default",
  nonClearableFilters = [],
  dependantFilter,
}) => {
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [recentFilters, setRecentFilters] = useLocalStorage(
    `recent_filters_${recentStoreKey}`,
    []
  );

  const router = useRouter();

  const visibleFilters = useMemo(
    () =>
      filters.filter(
        (f) => !["created", "date_created", "is_live"].includes(f.field_name)
      ),
    [filters]
  );

  const isAnyFilterApplied = useMemo(
    () => filters.some(({ field_name }) => !!router.query[field_name]),
    [filters, router.query]
  );

  const applyFilter = (filter, value) => {
    if (!value) return removeFilter(filter);
    router.replace({
      query: {
        ...router.query,
        ...(filtersFor === "default" && { page: 1 }),
        [filter]: value,
      },
    });
  };

  const removeFilter = (filter) => {
    const { [filter]: _queryFiltered, ...restQuery } = router.query;
    router.replace({
      query: { ...restQuery, ...(filtersFor === "default" && { page: 1 }) },
    });
  };

  const clearFilters = () => {
    const { search } = router.query;
    router.replace({
      query: {
        ...(filtersFor === "default" && { page: 1 }),
        ...(search ? { search } : {}), //persist search if exists
      },
    });
  };

  const filterOptions = useMemo(
    () =>
      visibleFilters
        .filter((filter) => !customDateFilters.includes(filter.field_name))
        .map((filter) => [
          filter.field_name,
          {
            ...filter,
            label:
              backendKeyMap[filter.field_name] ??
              snakeCaseToHumanReadable(filter.field_name),
          },
        ]),
    [visibleFilters, customDateFilters]
  );

  useEffect(() => {
    if (!open) {
      setActiveFilter(null);
    }
  }, [open]);

  const appliedFilters = useMemo(() => {
    const queryKeys = Object.keys(router.query);
    return queryKeys
      .filter((filter) => !customDateFilters.includes(filter))
      .map((key) => visibleFilters.find((filter) => filter.field_name === key))
      .filter(Boolean);
  }, [router.query, visibleFilters, customDateFilters]);

  const isCustomDateFilterApplied = useMemo(() => {
    return customDateFilters.some((filter) => filter in router.query);
  }, [router.query, customDateFilters]);

  const withCustomDateFilter = useMemo(() => {
    return customDateFilters.every((filter) =>
      filters.find((f) => f.field_name === filter)
    );
  }, [filters, customDateFilters]);

  if (!visibleFilters.length) return null;

  return (
    <FiltersProvider
      value={{
        filters,
        recentStoreKey,
        customDateFilters,
        filtersFor,
        nonClearableFilters,
        dependantFilter,
      }}
    >
      <div className="flex items-start gap-4 pb-4">
        <div className="flex flex-1 flex-wrap items-start gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              className={cn(
                "flex h-6 shrink-0 items-center gap-1 rounded-lg border border-transparent px-2 py-1 text-xs font-medium text-foundations-subtle outline-none hover:border-foundations-bold hover:bg-foundations-subtlest focus:border-info-bold focus:ring-1 focus:ring-info-bold",
                open && "!border-info-bold ring-1 ring-info-bold"
              )}
            >
              <FilterIcon className="h-4 w-4" /> Filter
            </PopoverTrigger>

            <FilterPopover>
              {!activeFilter && (
                <FilterSearchPopoverContent
                  recentFilters={recentFilters}
                  options={filterOptions}
                  onSelect={(filter) => {
                    setActiveFilter(filter);
                    setRecentFilters(
                      [...new Set([filter.field_name, ...recentFilters])].slice(
                        0,
                        MAX_RECENT_FILTERS
                      )
                    );
                  }}
                  setIsOpen={setOpen}
                />
              )}

              {activeFilter && (
                <FilterFieldContent
                  label={activeFilter.label}
                  value={router.query[activeFilter.field_name]}
                  filter={activeFilter}
                  onApplyFilter={applyFilter}
                  setIsOpen={setOpen}
                />
              )}
            </FilterPopover>
          </Popover>

          {appliedFilters.map((filter, index) => (
            <FilterField
              key={filter.field_type + index}
              filter={filter}
              value={router.query[filter.field_name]}
              onRemoveFilter={removeFilter}
              onApplyFilter={applyFilter}
            />
          ))}

          {isCustomDateFilterApplied && <CustomDateFilterField />}

          {isAnyFilterApplied && (
            <Button
              variant="tertiary"
              className="flex h-6 items-center px-3 py-0.5 text-xs font-medium text-info hover:bg-foundations-subtlest/70 hover:text-info"
              onClick={clearFilters}
            >
              <XIcon className="!h-4 !w-4" />
              Clear filters
            </Button>
          )}
        </div>

        {withCustomDateFilter && <DateFilters onApplyFilter={applyFilter} />}
      </div>
    </FiltersProvider>
  );
};

const useCustomDateFilter = () => {
  const router = useRouter();
  const createdAfter = router.query.created_after;
  const createdBefore = router.query.created_before;

  const { today, lastWeek, last30Days } = useMemo(() => {
    const now = new Date();
    return {
      today: format(now, useDateInput.valueFormatString),
      lastWeek: format(subDays(now, 7), useDateInput.valueFormatString),
      last30Days: format(subDays(now, 30), useDateInput.valueFormatString),
    };
  }, []);

  const isTodaySelected = !createdBefore && createdAfter === today;
  const isLastWeekSelected = !createdBefore && createdAfter === lastWeek;
  const isLast30DaysSelected = !createdBefore && createdAfter === last30Days;
  const isCustomSelected =
    !isTodaySelected &&
    !isLastWeekSelected &&
    !isLast30DaysSelected &&
    (createdBefore || createdAfter);

  return {
    createdBefore,
    createdAfter,
    today,
    lastWeek,
    last30Days,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isCustomSelected,
  };
};

const useDashboardCustomDateFilter = () => {
  const router = useRouter();
  const startDate = router.query.start_date;
  const endDate = router.query.end_date;

  const { today, lastWeek, last30Days } = useMemo(() => {
    const now = new Date();
    return {
      today: format(now, useDateInput.valueFormatString),
      lastWeek: format(subDays(now, 7), useDateInput.valueFormatString),
      last30Days: format(subDays(now, 30), useDateInput.valueFormatString),
    };
  }, []);

  const isTodaySelected = startDate === today && endDate === today;
  const isLastWeekSelected = startDate === lastWeek && endDate === today;
  const isLast30DaysSelected = startDate === last30Days && endDate === today;
  const isCustomSelected =
    !isTodaySelected &&
    !isLastWeekSelected &&
    !isLast30DaysSelected &&
    (startDate || endDate);

  return {
    startDate,
    endDate,
    today,
    lastWeek,
    last30Days,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isCustomSelected,
  };
};

const FiltersFor = {
  dashboard: useDashboardCustomDateFilter,
  default: useCustomDateFilter,
};

const CustomDateFilterField = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const { filtersFor } = useFiltersContext();

  const {
    createdBefore,
    createdAfter,
    startDate,
    endDate,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isCustomSelected,
  } = FiltersFor[filtersFor]();

  const { operator, formattedDate } = useCustomDateFormat({
    from: filtersFor === "default" ? createdAfter : startDate,
    to: filtersFor === "default" ? createdBefore : endDate,
  });

  return (
    <div className="flex h-6 shrink-0 text-xs font-medium text-foundations-subtle [&>:not(:first-child)]:-ml-px">
      <FilterChip className="flex items-center gap-1 capitalize">
        <ClockIcon className="h-4 w-4" />
        Date
      </FilterChip>

      <FilterChip>{isCustomSelected ? operator : "is"}</FilterChip>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <FilterChip interactive asChild className="text-foundations-bold">
          <PopoverTrigger>
            {isTodaySelected
              ? "Today"
              : isLastWeekSelected
                ? "Last week"
                : isLast30DaysSelected
                  ? "Last 30 days"
                  : formattedDate}
          </PopoverTrigger>
        </FilterChip>

        <FilterPopover>
          <CustomDatePopoverContent
            createdAfter={filtersFor === "default" ? createdAfter : startDate}
            createdBefore={filtersFor === "default" ? createdBefore : endDate}
            onClose={() => setIsOpen(false)}
          />
        </FilterPopover>
      </Popover>

      <FilterChip interactive asChild className="p-1 text-foundations-subtler">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const {
              created_after: _after,
              created_before: _before,
              start_date: _start,
              end_date: _end,
              ...restQuery
            } = router.query;
            router.replace({
              query: {
                ...restQuery,
                ...(filtersFor === "default" && { page: 1 }),
              },
            });
          }}
        >
          <XIcon className="h-4 w-4" />
        </button>
      </FilterChip>
    </div>
  );
};

const FilterSearchPopoverContent = ({
  options,
  onSelect,
  recentFilters,
  setIsOpen,
}) => {
  return (
    <Combobox
      options={options}
      recentFilters={recentFilters}
      itemToString={(item) => item?.[1].label}
      filterFn={([_value, { label }], searchTerm) => {
        return label
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      }}
      onSelect={([_, filter]) => onSelect(filter)}
      setIsOpen={setIsOpen}
    >
      <div className="flex items-center gap-3 px-4 pb-1 pt-3">
        <Combobox.Input />

        <PopoverClose className="text-foundations-subtler hover:text-foundations-subtle focus:text-info focus:outline-none">
          <XCircleIcon className="h-5 w-5" />
        </PopoverClose>
      </div>

      <div className="flex-1 scroll-pb-2  overflow-y-auto pb-2 pt-0.5">
        <section>
          <Combobox.Options renderOption={FilterOption} />
        </section>
      </div>
    </Combobox>
  );
};

const FilterOption = ({ field_name, label }) => {
  const Icon = filterIcons[field_name];
  return (
    <div className="flex items-center gap-3 capitalize ">
      {Icon && <Icon className="h-5 w-5 shrink-0 text-foundations-subtler" />}
      {label}
    </div>
  );
};

/**
 * InPlaceFilters component
 *
 * @param {Object} props - Component props
 * @param {any} props.filters - The filters to display or apply
 * @param {string} [props.recentStoreKey] - Key used for storing/retrieving recent filters
 * @param {boolean} [props.showLabel=true] - Whether to show the "Filter:" label
 * @param {boolean} [props.showClearFilters=true] - Whether to show the "Clear filters" button
 * @param {Object} props.appliedFilters - The currently applied filters
 * @param {Function} props.customApplyFilter - Function to apply a filter
 * @param {Function} props.customRemoveFilter - Function to remove a filter
 * @param {Function} props.customClearFilters - Function to clear all filters
 *  @param {string[]} [props.customDateFilters=customIntentAndTrxDateFilters] - Optional custom date filters
 * @param {"default" | "dashboard"} [props.filtersFor="default"] - Context for which filters apply
 * @param {string[]} [props.nonClearableFilters] - List of filter keys that cannot be cleared
 * @param {{name: string, filterBy: string}} [props.dependantFilter] - Optional filter dependency
 */
export const InPlaceFilters = ({
  filters,
  showLabel = true,
  showClearFilters = true,
  customDateFilters = customIntentAndTrxDateFilters,
  appliedFilters,
  recentStoreKey,
  filtersFor = "default",
  nonClearableFilters = [],
  customApplyFilter,
  customRemoveFilter,
  customClearFilters,
  dependantFilter,
}) => {
  const visibleFilters = useMemo(
    () =>
      filters.filter(
        (f) => !["created", "date_created", "is_live"].includes(f.field_name)
      ),
    [filters]
  );

  const applyFilter = (filter, value) => {
    if (!value) return removeFilter(filter);
    customApplyFilter(filter, value);
  };

  const removeFilter = (filter) => {
    customRemoveFilter(filter);
  };

  const clearFilters = () => {
    customClearFilters();
  };

  if (!visibleFilters.length) return null;

  return (
    <FiltersProvider
      value={{
        filters,
        recentStoreKey,
        customDateFilters,
        filtersFor,
        nonClearableFilters,
        dependantFilter,
      }}
    >
      <div className="flex flex-1 items-center">
        {showLabel && (
          <div className="mr-2 mt-1 text-foundations-subtler">Filter:</div>
        )}
        <div className="flex flex-1 flex-wrap gap-2">
          {visibleFilters.map((filter, index) => (
            <FilterField
              key={filter.field_type + index}
              filter={filter}
              value={appliedFilters[filter.field_name]}
              onRemoveFilter={removeFilter}
              onApplyFilter={applyFilter}
              emptyFallback="..."
            />
          ))}
        </div>
        <div className="flex items-center justify-end text-foundations-subtle hover:text-foundations-bold">
          {showClearFilters && (
            <button className="flex items-center" onClick={clearFilters}>
              <XIcon className="h-5 text-xs" />{" "}
              <span className="pl-1">Clear filters</span>
            </button>
          )}
        </div>
      </div>
    </FiltersProvider>
  );
};

const DateField = ({ value, updateFilter, filter }) => {
  const { inputRef, inputProps, setInputValue, month, setMonth } = useDateInput(
    {
      onSelect: (day) => updateFilter(filter.field_name, day),
      selected: value,
    }
  );

  const handleDaySelect = (date) => {
    if (date) {
      updateFilter(
        filter.field_name,
        format(date, useDateInput.valueFormatString)
      );
    } else {
      updateFilter(filter.field_name, "");
      setInputValue("");
    }
  };

  return (
    <div className="flex flex-col">
      <DateInput
        ref={inputRef}
        className="px-2"
        icon={<CalendarIcon className="h-5 w-5" />}
        {...inputProps}
      />
      <Calendar
        className="self-center"
        mode="single"
        selected={
          value
            ? parse(value, useDateInput.valueFormatString, new Date())
            : undefined
        }
        onSelect={handleDaySelect}
        disablePortal
        month={month}
        onMonthChange={setMonth}
      />
    </div>
  );
};

const DateFilters = () => {
  const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
  const router = useRouter();
  const { filtersFor } = useFiltersContext();

  const {
    createdAfter,
    createdBefore,
    startDate,
    endDate,
    today,
    lastWeek,
    last30Days,
    isTodaySelected,
    isLastWeekSelected,
    isLast30DaysSelected,
    isCustomSelected,
  } = FiltersFor[filtersFor]();

  return (
    <div className="flex shrink-0 gap-2 rounded-md border border-foundations-subtle p-0.5 text-foundations-subtle">
      <Popover open={isCustomPopoverOpen} onOpenChange={setIsCustomPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-1 rounded px-1 py-px text-xs font-medium hover:bg-info-subtler hover:text-info",
              isCustomSelected && "bg-info-subtler text-info"
            )}
          >
            {isCustomSelected ? (
              <>
                <CalendarIcon className="h-4 w-4" />{" "}
                <CustomDateFormatted
                  from={filtersFor === "default" ? createdAfter : startDate}
                  to={filtersFor === "default" ? createdBefore : endDate}
                />
              </>
            ) : (
              "Custom"
            )}
          </button>
        </PopoverTrigger>
        <FilterPopover align="center">
          <CustomDatePopoverContent
            createdAfter={filtersFor === "default" ? createdAfter : startDate}
            createdBefore={filtersFor === "default" ? createdBefore : endDate}
            onClose={() => setIsCustomPopoverOpen(false)}
          />
        </FilterPopover>
      </Popover>
      <button
        type="button"
        className={cn(
          "rounded px-1 py-px text-xs font-medium hover:bg-info-subtler hover:text-info",
          isTodaySelected && "bg-info-subtler text-info"
        )}
        onClick={() => {
          if (filtersFor === "dashboard") {
            const {
              start_date: _start,
              end_date: _end,
              ...restQuery
            } = router.query;
            router.replace({
              query: {
                ...restQuery,
                start_date: today,
                end_date: today,
              },
            });
            return;
          }

          const { created_before: _before, ...restQuery } = router.query;
          router.replace({
            query: {
              ...restQuery,
              created_after: today,
              page: 1,
            },
          });
        }}
      >
        Today
      </button>
      <button
        type="button"
        className={cn(
          "rounded px-1 py-px text-xs font-medium hover:bg-info-subtler hover:text-info",
          isLastWeekSelected && "bg-info-subtler text-info"
        )}
        onClick={() => {
          if (filtersFor === "dashboard") {
            const {
              start_date: _start,
              end_date: _end,
              ...restQuery
            } = router.query;
            router.replace({
              query: {
                ...restQuery,
                start_date: lastWeek,
                end_date: today,
              },
            });
            return;
          }

          const { created_before: _before, ...restQuery } = router.query;
          router.replace({
            query: {
              ...restQuery,
              created_after: lastWeek,
              page: 1,
            },
          });
        }}
      >
        Last week
      </button>
      <button
        type="button"
        className={cn(
          "rounded px-1 py-px text-xs font-medium hover:bg-info-subtler hover:text-info",
          isLast30DaysSelected && "bg-info-subtler text-info"
        )}
        onClick={() => {
          if (filtersFor === "dashboard") {
            const {
              start_date: _start,
              end_date: _end,
              ...restQuery
            } = router.query;
            router.replace({
              query: {
                ...restQuery,
                start_date: last30Days,
                end_date: today,
              },
            });
            return;
          }

          const { created_before: _before, ...restQuery } = router.query;
          router.replace({
            query: {
              ...restQuery,
              created_after: last30Days,
              page: 1,
            },
          });
        }}
      >
        Last 30 days
      </button>
    </div>
  );
};

const useCustomDateFormat = ({ from, to }) => {
  if (!from) {
    const endDate = new Date(to);
    return {
      operator: "before",
      formattedDate: `${format(endDate, "dd MMM yyyy")}`,
    };
  }

  if (!to) {
    const startDate = new Date(from);
    return {
      operator: "after",
      formattedDate: `${format(startDate, "dd MMM yyyy")}`,
    };
  }

  const startDate = new Date(from);
  const endDate = new Date(to);
  const sameYear = isSameYear(startDate, endDate);

  return {
    operator: "between",
    formattedDate: `${format(
      startDate,
      `dd MMM ${sameYear ? "" : "yyyy"}`
    )} - ${format(endDate, `dd MMM${sameYear ? ", yyyy" : " yyyy"}`)}`,
  };
};

const CustomDateFormatted = ({ from, to }) => {
  const { operator, formattedDate } = useCustomDateFormat({ from, to });
  if (operator === "between") return formattedDate;
  return `${operator} ${formattedDate}`;
};

const CustomDatePopoverContent = ({ createdBefore, createdAfter, onClose }) => {
  const router = useRouter();
  const { filtersFor } = useFiltersContext();
  const defaultTabOpen = useMemo(() => {
    if (createdBefore && createdAfter) return 0;
    if (createdBefore) return 1;
    if (createdAfter) return 2;
    return 0;
  }, [createdBefore, createdAfter]);

  return (
    <Tab.Group defaultIndex={defaultTabOpen}>
      <Tab.List className="flex items-center justify-center gap-4 border-b border-foundations-subtle text-sm font-medium">
        <Tab className="-mb-px border-b-2 border-transparent px-2 py-2.5 text-foundations-subtle aria-selected:border-info-bold aria-selected:text-foundations-bolder">
          Range
        </Tab>
        {filtersFor === "default" && (
          <>
            <Tab className="-mb-px border-b-2 border-transparent px-2 py-2.5 text-foundations-subtle aria-selected:border-info-bold aria-selected:text-foundations-bolder">
              Before
            </Tab>
            <Tab className="-mb-px border-b-2 border-transparent px-2 py-2.5 text-foundations-subtle aria-selected:border-info-bold aria-selected:text-foundations-bolder">
              After
            </Tab>
          </>
        )}
      </Tab.List>

      <Tab.Panels className="pt-4">
        <Tab.Panel className="flex flex-col gap-4">
          <RangeCustomDate from={createdAfter} to={createdBefore} />
        </Tab.Panel>
        {filtersFor === "default" && (
          <>
            <Tab.Panel className="flex flex-col gap-4">
              <SingleCustomDate
                value={createdBefore}
                label="Before"
                onChange={(value) => {
                  const { created_after: _after, ...restQuery } = router.query;
                  router.replace({
                    query: {
                      ...restQuery,
                      created_before: value,
                      page: 1,
                    },
                  });
                }}
                onClose={onClose}
              />
            </Tab.Panel>
            <Tab.Panel className="flex flex-col gap-4">
              <SingleCustomDate
                value={createdAfter}
                label="After"
                onChange={(value) => {
                  const { created_before: _before, ...restQuery } =
                    router.query;
                  router.replace({
                    query: {
                      ...restQuery,
                      created_after: value,
                      page: 1,
                    },
                  });
                }}
                onClose={onClose}
              />
            </Tab.Panel>
          </>
        )}
      </Tab.Panels>
    </Tab.Group>
  );
};

const SingleCustomDate = ({ value, label, onChange, onClose }) => {
  const { inputRef, inputProps, setInputValue, month, setMonth } = useDateInput(
    {
      onSelect: (day) => onChange(day),
      selected: value,
    }
  );

  const handleDaySelect = (date) => {
    if (date) {
      onChange(format(date, useDateInput.valueFormatString));
    } else {
      onChange("");
      setInputValue("");
    }
    onClose();
  };

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (isValidDate(inputProps.value)) {
        const date = parse(
          inputProps.value,
          useDateInput.inputFormatString,
          new Date()
        );
        onChange(format(date, useDateInput.valueFormatString));
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [inputProps.value]);

  return (
    <>
      <div className="flex items-center gap-1 self-center">
        <p className="w-24  text-sm font-medium text-foundations-bold">
          {label} date
        </p>
        <DateInput
          ref={inputRef}
          {...inputProps}
          className="w-full flex-1"
          placeholder="Date"
        />
      </div>
      <Calendar
        className="self-center"
        mode="single"
        selected={
          value
            ? parse(value, useDateInput.valueFormatString, new Date())
            : undefined
        }
        onSelect={handleDaySelect}
        disablePortal
        month={month}
        onMonthChange={setMonth}
      />
    </>
  );
};

const RangeCustomDate = ({ from, to }) => {
  const router = useRouter();
  const { filtersFor } = useFiltersContext();
  const [selectedDateRange, setSelectedDateRange] = useState(() => ({
    from: from
      ? parse(from, useDateInput.valueFormatString, new Date())
      : undefined,
    to: to ? parse(to, useDateInput.valueFormatString, new Date()) : undefined,
  }));

  const {
    inputRef: startInputRef,
    inputProps: startInputProps,
    month,
    setMonth,
  } = useDateInput({
    selected: selectedDateRange?.from
      ? format(selectedDateRange.from, useDateInput.valueFormatString)
      : undefined,
  });
  const { inputRef: endInputRef, inputProps: endInputProps } = useDateInput({
    selected: selectedDateRange?.to
      ? format(selectedDateRange.to, useDateInput.valueFormatString)
      : undefined,
  });

  useUpdateEffect(() => {
    if (selectedDateRange?.from && selectedDateRange?.to) {
      router.replace({
        query: {
          ...router.query,
          ...(filtersFor === "default"
            ? {
                created_after: format(
                  selectedDateRange.from,
                  useDateInput.valueFormatString
                ),
                created_before: format(
                  selectedDateRange.to,
                  useDateInput.valueFormatString
                ),
              }
            : {
                start_date: format(
                  selectedDateRange.from,
                  useDateInput.valueFormatString
                ),
                end_date: format(
                  selectedDateRange.to,
                  useDateInput.valueFormatString
                ),
              }),
        },
      });
    }
  }, [selectedDateRange]);

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (isValidDate(startInputProps.value)) {
        const date = parse(
          startInputProps.value,
          useDateInput.inputFormatString,
          new Date()
        );
        setSelectedDateRange((prev) => ({
          ...prev,
          from: date,
        }));
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [startInputProps.value]);

  useUpdateEffect(() => {
    const timerId = setTimeout(() => {
      if (isValidDate(endInputProps.value)) {
        const date = parse(
          endInputProps.value,
          useDateInput.inputFormatString,
          new Date()
        );

        setSelectedDateRange((prev) => ({
          ...prev,
          to: date,
        }));
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [endInputProps.value]);

  return (
    <>
      <div className="flex items-center gap-1 self-center">
        <p className="w-24  text-sm font-medium text-foundations-bold">
          Start date
        </p>
        <DateInput
          ref={startInputRef}
          {...startInputProps}
          className="w-full flex-1"
          placeholder="Date"
        />
      </div>
      <div className="flex items-center gap-1 self-center">
        <p className="w-24  text-sm font-medium text-foundations-bold">
          End date
        </p>
        <DateInput
          ref={endInputRef}
          {...endInputProps}
          className="w-full flex-1"
          placeholder="Date"
        />
      </div>
      <Calendar
        className="self-center"
        mode="range"
        selected={selectedDateRange}
        onSelect={(value) => {
          if (!value) {
            const {
              created_after: _after,
              created_before: _before,
              start_date: _start,
              end_date: _end,
              page: _page,
              ...restQuery
            } = router.query;
            router.replace({
              query: {
                ...restQuery,
                ...(filtersFor === "default" && { page: 1 }),
              },
            });
          }
          setSelectedDateRange(value);
        }}
        disablePortal
        month={month}
        onMonthChange={setMonth}
        toDate={new Date()}
      />
    </>
  );
};

const filterIcons = {
  is_live: StatusOnlineIcon,
  is_active: StatusOnlineIcon,
  status: BadgeCheckIcon,
  created_before: ClockIcon,
  created_after: ClockIcon,
  plan: PercentageSquareIcon,
  start_date: ClockIcon,
  renewal_date_from: ClockIcon,
  renewal_date_to: ClockIcon,
  amount_less_than: DollarCircleIcon,
  amount_more_than: DollarCircleIcon,
  provider_live_status: StatusOnlineIcon,
  type: MoneySendIcon,
  billable: EmptyWalletTickIcon,
  recovered: OptimizationIcon,
  currency: DollarSquareIcon,
  settlement_currency: DollarSquareIcon,
  is_wallet: WalletIcon,
  operation_type: MoneyReciveIcon,
  service_provider: PaymentProviderIcon,
  template_type: PaymentProviderIcon,
  provider: PaymentProviderIcon,
  custom_fields: CodeCircleIcon,
  response_status_code: CodeCircleIcon,
  intent: DocumentMetaIcon,
  intent_id: DocumentMetaIcon,
  customer: UsersIcon,
  uuid: ChipIcon,
  id: ChipIcon,
  obj_pk: ChipIcon,
  obj_type: HierarchySquare2Icon,
  transaction_uuid: TransactionsIcon,
  operation: MoneyReciveIcon,
  initiation: MoneyReciveIcon,
  subscription: RefreshIcon,
  recurrence: RefreshIcon,
  custom_recurrence: RefreshIcon,
  merchant_reference: DocumentCodeIcon,
  risk_level: SecurityCardIcon,
  ip_address: GlobeAltIcon,
  account: UserCircleIcon,
  account__id: UserCircleIcon,
  accounts: UserCircleIcon,
  organization: LibraryIcon,
  organization__id: LibraryIcon,
  method: CloudIcon,
  payment_method: PayInProviderIcon,
  customer_phone_number: PhoneIcon,
  customer_email: MailIcon,
  customer_name: DocumentTextIcon,
  settlement_file: DocumentTextIcon,
  card_type: CardLookupIcon,
  card_last_4: CardLookupIcon,
  card_expiry_month: CardLookupIcon,
  card_expiry_year: CardLookupIcon,
  card_bin: CardLookupIcon,
  card_brand: CardLookupIcon,
  card_country: GlobeIcon,
  reconciliation_status: StatusOnlineIcon,
  method_id: CardEditIcon,
  connection_id: ProviderLookupIcon,
};

// ------ Combobox ------
const ComboboxContext = createContext(null);

const Combobox = ({
  children,
  options,
  recentFilters,
  filterFn,
  itemToString = (item) => item?.[1],
  onSelect,
  setIsOpen,
  isMultiple,
  selected,
  inputValue,
  onInputValueChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filterFnRef = useRef(filterFn);
  filterFnRef.current = filterFn;

  const filteredOptions = useMemo(
    () =>
      !searchTerm || !filterFnRef.current
        ? options
        : options?.filter((item) => filterFnRef.current(item, searchTerm)),
    [options, searchTerm]
  );

  const recentOptions = useMemo(() => {
    if (options.length <= 5 || !recentFilters?.length) return null;
    return recentFilters
      .map((filter) => filteredOptions.find(([value]) => value === filter))
      .filter(Boolean);
  }, [options, filteredOptions, recentFilters]);

  const selectedItem = useMemo(() => {
    if (!options?.length) return [];

    if (isMultiple) {
      return (
        selected
          ?.map?.((v) => options.find((option) => option[0] === String(v)))
          ?.filter((v) => v !== undefined) || []
      );
    }

    return options.find((option) => option[0] === String(selected)) || null;
  }, [options, isMultiple, selected]);

  const items = useMemo(
    () =>
      recentOptions ? [...recentOptions, ...filteredOptions] : filteredOptions,
    [recentOptions, filteredOptions]
  );

  const { getInputProps, getMenuProps, getItemProps, highlightedIndex } =
    useCombobox({
      selectedItem,
      isOpen: true,
      items,
      onIsOpenChange: ({ isOpen, type }) => {
        if (type === useCombobox.stateChangeTypes.InputKeyDownEscape)
          setIsOpen?.(!!isOpen);
      },
      inputValue: inputValue ?? searchTerm,
      onInputValueChange: ({ inputValue }) =>
        onInputValueChange
          ? onInputValueChange(inputValue)
          : setSearchTerm(inputValue ?? ""),
      itemToString,
      onSelectedItemChange: ({ selectedItem }) => onSelect(selectedItem),
      stateReducer: isMultiple ? multipleStateReducer : singleStateReducer,
      scrollIntoView,
    });

  return (
    <ComboboxContext.Provider
      value={{
        getInputProps,
        getMenuProps,
        getItemProps,
        highlightedIndex,
        filteredOptions,
        isMultiple,
        recentOptions,
      }}
    >
      {children}
    </ComboboxContext.Provider>
  );
};

Combobox.Input = function ComboboxInput() {
  const { getInputProps } = useContext(ComboboxContext);
  return (
    <Input
      autoFocus
      icon={<SearchIcon />}
      placeholder="Search filters"
      containerClassName="flex-1"
      nativeChangeEvent
      {...getInputProps()}
    />
  );
};

Combobox.Options = function ComboboxOptions({
  renderOption,
  isSelected,
  showOptions = true,
  children,
}) {
  const {
    filteredOptions,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    isMultiple,
    recentOptions,
  } = useContext(ComboboxContext);

  return (
    <ChoicesList
      choices={filteredOptions}
      recentOptions={recentOptions}
      render={renderOption}
      getItemProps={getItemProps}
      getMenuProps={getMenuProps}
      highlightedIndex={highlightedIndex}
      isSelected={isSelected}
      isMultiple={isMultiple}
      showOptions={showOptions}
    >
      {children}
    </ChoicesList>
  );
};

const multipleStateReducer = (state, actionAndChanges) => {
  const { changes, type } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
      return {
        ...changes,
        inputValue: state.inputValue || "",
      };

    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return {
        ...changes,
        isOpen: true, // keep dropdown open after selection
        highlightedIndex: state.highlightedIndex, // keep highlighted index position
        inputValue: state.inputValue, // keep search query
      };

    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...changes,
        selectedItem: state.selectedItem,
        highlightedIndex: state.highlightedIndex,
        inputValue: state.inputValue,
      };

    case useCombobox.stateChangeTypes.InputClick:
      return {
        ...changes,
        isOpen: true,
      };
    default:
      return changes;
  }
};

const singleStateReducer = (state, actionAndChanges) => {
  const { changes, type } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
      return {
        ...changes,
        inputValue: state.inputValue || "",
      };

    // reset selected item if reselected again
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return {
        ...changes,
        selectedItem:
          state.selectedItem?.[0] === changes.selectedItem?.[0]
            ? null
            : changes.selectedItem,
      };

    case useCombobox.stateChangeTypes.InputBlur:
      return {
        ...changes,
        selectedItem: state.selectedItem,
        highlightedIndex: state.highlightedIndex,
        inputValue: state.inputValue,
      };

    case useCombobox.stateChangeTypes.InputClick:
      return {
        ...changes,
        isOpen: true,
      };
    default:
      return changes;
  }
};

const scrollIntoView = (node) => {
  requestAnimationFrame
    ? requestAnimationFrame(() => {
        node.scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
      })
    : setTimeout(() => {
        node.scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
      }, 0);
};
